import React from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import {
  Grid,
  List,
  ListItem,
  Divider,
  Chip,
  Typography,
  ListItemAvatar,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
} from "@mui/material";
import PairingEventDialog from "./PairingEventDialog";
import formatDate from "../../../utilities/formatDate";
import { EventContext } from "../../../contexts/EventContext";
import request from "../../../utilities/requests";

const channelLogos = {
  tnfm: "https://d19w6ranc5yet4.cloudfront.net/tnfm/tnfm-logo.png",
  eventbrite: "https://d19w6ranc5yet4.cloudfront.net/tnfm/eb-logo.png",
};

const PairingEvents = () => {
  const navigate = useNavigate();
  const { userData, pairingEvents, setPairingEvents } =
    React.useContext(EventContext);
  const [filteredEvents, setFilteredEvents] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [showFilter, setShowFilter] = React.useState(false);
  const [orgList, setOrgList] = React.useState([]);
  const [selectedOrg, setSelectedOrg] = React.useState(null);
  const [all, setAll] = React.useState(false);

  React.useEffect(() => {
    if (!loaded) {
      getPairingEvents();
      setLoaded(true);
    }
  });

  React.useEffect(() => {
    if (pairingEvents && pairingEvents.length > 0) {
      setFilteredEvents(pairingEvents);
    }
  }, [pairingEvents]);

  React.useEffect(() => {
    if (userData?.role === "superadmin") {
      setShowFilter(true);
    }
  }, [userData, orgList, pairingEvents]);

  const getPairingEvents = async (allValue) => {
    let url = "pairingevent";
    if (allValue === true) {
      url = url + "?all=true";
    }
    const result = await request(url);

    if (result.success) {
      setPairingEvents(result.pairingEvents);
      setFilteredEvents(result.pairingEvents);
      if (result?.orgData) {
        setOrgList(result.orgData);
      }
    }
  };

  const getListItemColor = (pairingEvent) => {
    try {
      const userTimezone = moment.tz.guess(); // e.g., "America/Los_Angeles"
      const now = new moment().tz(userTimezone);
      const date = moment(pairingEvent?.event?.date).tz(userTimezone);
      const isSameDay = now.isSame(date, "day");
      if (isSameDay) {
        return "rgb(222 221 221)";
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const handleClickOpen = (pairingEvent) => {
    navigate(`/eventcontroller/${pairingEvent?.event?._id}`);
  };

  const handleAll = () => {
    setAll(!all);
    getPairingEvents(!all);
  };

  const handleOrgChange = (orgId) => {
    if (orgId === "none") {
      setSelectedOrg(null);
      setFilteredEvents(pairingEvents);
    } else {
      setSelectedOrg(orgId);
      const filtered = pairingEvents.filter((f) => f?.event?.org === orgId);
      setFilteredEvents(filtered);
    }
  };

  const renderChannels = (channels) => {
    try {
      if (channels.length > 0) {
        return (
          <Grid
            item
            xs={6}
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Stack direction="row" spacing={1}>
              {channels.map((channel) => {
                return (
                  <Box
                    key={channel}
                    component="img"
                    src={channelLogos[channel]}
                    alt={`${channel} logo`}
                    sx={{
                      height: 16,
                      width: "auto",
                      objectFit: "contain",
                      display: "block",
                      maxWidth: "100%",
                    }}
                  />
                );
              })}
            </Stack>
          </Grid>
        );
      }

      return null;
    } catch (e) {
      return null;
    }
  };

  return (
    <>
      <Grid container flexDirection="column">
        <Grid
          item
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 20,
          }}
        >
          <Chip
            label="All"
            variant={all ? "" : "outlined"}
            style={{ width: 50 }}
            onClick={handleAll}
          />
          {showFilter ? (
            <FormControl fullWidth variant="outlined" sx={{ width: 250 }}>
              <InputLabel sx={{ top: -5 }}>Org Filter</InputLabel>
              <Select
                value={selectedOrg || ""}
                label="Org"
                onChange={(e) => handleOrgChange(e.target.value)}
                sx={{ height: 40 }}
              >
                <MenuItem value={"none"}>None</MenuItem>
                {orgList.map((org) => {
                  return (
                    <MenuItem key={org._id} value={org._id}>
                      {org.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : null}
        </Grid>
        <Grid item>
          <List>
            {filteredEvents.map((pairingEvent) => {
              return (
                <div key={pairingEvent._id} style={{ padding: 0 }}>
                  <ListItem
                    style={{
                      backgroundColor: getListItemColor(pairingEvent),
                      padding: 10,
                    }}
                  >
                    {pairingEvent?.logo ? (
                      <ListItemAvatar style={{ marginRight: 20 }}>
                        <Avatar src={pairingEvent.logo} />
                      </ListItemAvatar>
                    ) : null}
                    <a
                      href={`/eventcontroller/${pairingEvent?.event?._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        width: "100%",
                        display: "block",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleClickOpen(pairingEvent);
                      }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            {pairingEvent?.event?.name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            {formatDate(pairingEvent?.event?.date)}
                          </Typography>
                        </Grid>
                        {renderChannels(pairingEvent?.channels)}
                      </Grid>
                    </a>
                  </ListItem>
                  <Divider />
                </div>
              );
            })}
          </List>
        </Grid>
      </Grid>
    </>
  );
};

export default PairingEvents;
